import { computed, ref } from 'vue';

/**
 * Shared filter/sort/search criteria for student task lists (sections,
 * courses, steps).
 *
 * The criteria are the single source of truth for BOTH projections:
 * - `toListQueryParams` — sent to the backend so pagination stays accurate
 * - `matchesListCriteria` / `compareByListCriteria` — mirrored over the Pinia
 *   ORM cache so the visible list obeys the same rules instantly
 * The two must never be edited independently; the backend whitelists the same
 * status/sort keys (see user-section controller `findByUser`).
 */

export type ListItemStatus = 'not_started' | 'in_progress' | 'completed';

export type ListSortKey = 'recent' | 'name' | 'score_asc' | 'score_desc';

export const LIST_SORT_KEYS: ListSortKey[] = [
  'recent',
  'name',
  'score_asc',
  'score_desc',
];

/** Common shape each list adapts its rows into (see `toFilterableItem`). */
export interface FilterableListItem {
  name: string;
  startDate?: string | null;
  completionDate?: string | null;
  score?: number | null;
  timeAccumulator?: number | null;
  /** Entries in the progress JSON — the client-only "started" signal. */
  progressCount?: number;
}

export interface ListFilterCriteria {
  /** Statuses to SHOW. Empty = show nothing; all = show everything. */
  statuses: ListItemStatus[];
  sortBy: ListSortKey;
  search: string;
}

/**
 * Client projection of the "started" definition (hasSectionStarted /
 * hasCourseStarted): any recorded activity counts.
 */
function hasItemStarted(item: FilterableListItem): boolean {
  return Boolean(
    item.startDate ||
      item.completionDate ||
      (item.timeAccumulator || 0) > 0 ||
      (item.score || 0) > 0 ||
      (item.progressCount || 0) > 0,
  );
}

export function listItemStatus(item: FilterableListItem): ListItemStatus {
  if (item.completionDate) return 'completed';
  return hasItemStarted(item) ? 'in_progress' : 'not_started';
}

export function matchesListCriteria(
  item: FilterableListItem,
  criteria: ListFilterCriteria,
): boolean {
  // Selected = shown. An empty selection therefore shows nothing — the two
  // extremes (none picked / all picked) must not look the same but mean the
  // same thing.
  if (!criteria.statuses.includes(listItemStatus(item))) return false;

  const search = criteria.search.trim().toLowerCase();
  if (search && !item.name.toLowerCase().includes(search)) return false;

  return true;
}

// Missing dates sort after any real date under the "recent" sorts.
function dateRank(value?: string | null): number {
  return value ? new Date(value).getTime() : -Infinity;
}

// Unscored items always sort last — "lowest score first" means the weakest
// *scored* work, not the untouched tail.
function compareScores(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: 1 | -1,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return (a - b) * direction;
}

export function compareByListCriteria(
  a: FilterableListItem,
  b: FilterableListItem,
  sortBy: ListSortKey,
): number {
  switch (sortBy) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'score_asc':
      return compareScores(a.score, b.score, 1);
    case 'score_desc':
      return compareScores(a.score, b.score, -1);
    case 'recent':
    default:
      return (
        dateRank(b.completionDate) - dateRank(a.completionDate) ||
        dateRank(b.startDate) - dateRank(a.startDate)
      );
  }
}

export function toListQueryParams(
  criteria: ListFilterCriteria,
): Record<string, string> {
  const params: Record<string, string> = {};
  // Every item is in exactly one status, so all statuses selected = no
  // restriction (omit the param). An EMPTY selection matches nothing — call
  // sites skip the fetch entirely instead of asking the server for it.
  if (
    criteria.statuses.length > 0 &&
    criteria.statuses.length < ALL_STATUSES.length
  ) {
    params.status = criteria.statuses.join(',');
  }
  if (criteria.sortBy !== 'recent') params.sortBy = criteria.sortBy;
  const search = criteria.search.trim();
  if (search) params.search = search;
  return params;
}

const ALL_STATUSES: ListItemStatus[] = [
  'not_started',
  'in_progress',
  'completed',
];

/**
 * Hiding completed work is the DEFAULT student experience — finished tasks
 * leave the main view until the student explicitly shows them. Every page
 * entry starts here, deterministically (no persistence): the switch reads
 * "hide completed" and exactly these chips are filled.
 */
const DEFAULT_STATUSES: ListItemStatus[] = ['not_started', 'in_progress'];

function isDefaultSelection(statuses: ListItemStatus[]): boolean {
  return (
    statuses.length === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every((s) => statuses.includes(s))
  );
}

export function useListFilters() {
  // Single source of truth for status filtering: the chip selection
  // (selected = shown; empty = nothing). The hide-completed switch is a
  // derived view of it, so the two controls can never disagree.
  const selectedStatuses = ref<ListItemStatus[]>([...DEFAULT_STATUSES]);
  const sortBy = ref<ListSortKey>('recent');
  const search = ref('');

  const hideCompleted = computed({
    // "Completed is not among the shown statuses" — also true for an empty
    // selection, where completed (like everything else) is hidden.
    get: () => !selectedStatuses.value.includes('completed'),
    set: (value) => {
      if (value) {
        const remaining = selectedStatuses.value.filter(
          (s) => s !== 'completed',
        );
        // Turning the switch on means "show me my remaining work", so an
        // otherwise-empty selection seeds the default instead of nothing.
        selectedStatuses.value = remaining.length
          ? remaining
          : [...DEFAULT_STATUSES];
        return;
      }
      // Switching off means "show completed again": fill the completed chip.
      if (!selectedStatuses.value.includes('completed')) {
        selectedStatuses.value = [...selectedStatuses.value, 'completed'];
      }
    },
  });

  function toggleStatus(status: ListItemStatus) {
    selectedStatuses.value = selectedStatuses.value.includes(status)
      ? selectedStatuses.value.filter((s) => s !== status)
      : [...selectedStatuses.value, status];
  }

  const criteria = computed<ListFilterCriteria>(() => ({
    statuses: selectedStatuses.value,
    sortBy: sortBy.value,
    search: search.value,
  }));

  /** Criteria differing from the page default — for the filter-button badge. */
  const activeFilterCount = computed(() => {
    let count = 0;
    if (!isDefaultSelection(selectedStatuses.value)) count += 1;
    if (sortBy.value !== 'recent') count += 1;
    if (search.value.trim()) count += 1;
    return count;
  });

  function clearAll() {
    selectedStatuses.value = [...DEFAULT_STATUSES];
    sortBy.value = 'recent';
    search.value = '';
  }

  return {
    hideCompleted,
    selectedStatuses,
    sortBy,
    search,
    criteria,
    activeFilterCount,
    toggleStatus,
    clearAll,
  };
}
